// module_roots/Login.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../module_roots/AuthContext";

import {
  acceptSecureLogin,
  secureLoginPayload,
  upgradeLegacySession
} from "./RootScurityClient";

import {
  Eye,
  EyeOff,
  Lock,
  User,
  AlertCircle,
  X,
  CheckCircle,
  Activity,
  ArrowRight,
  Sun,
  Moon,
  Palette,
  Store,
  Pill,
  Building2,
  ShoppingCart,
  Coffee,
  Wrench,
  Car,
  Scissors,
  PaintBucket,
  Heart,
  Sparkles,
  Droplet,
  Syringe,
  Package,
  Scissors as ScissorsIcon,
  Shirt,
  Bike,
  Home,
  ChevronDown,
  ChevronUp,
  Globe,
  Settings
} from 'lucide-react';
import './Login.css';
import logo from '../assets/images/logo.png';

// ============================================
// ROLE TO DASHBOARD ROUTES MAPPING - SIMPLIFIED
// ============================================
const ROUTES = {
  'super_admin': '/DashboardSuperAdmin',
  'superadmin': '/DashboardSuperAdmin',
  
  // ALL BUSINESS ROLES - Unified Dashboard
  'admin_pos': '/Dashboard-Business-Manager',
  'manager_pos': '/Dashboard-Business-Manager',
  'user_pos': '/Dashboard-Business-Manager',
  'viewer_pos': '/Dashboard-Business-Manager',
  'salesman_pos': '/Dashboard-Business-Manager',
  
  'admin_po': '/Dashboard-Business-Manager',
  'manager_po': '/Dashboard-Business-Manager',
  'user_po': '/Dashboard-Business-Manager',
  'viewer_po': '/Dashboard-Business-Manager',
  
  'admin_fabrics': '/Dashboard-Business-Manager',
  'manager_fabrics': '/Dashboard-Business-Manager',
  'user_fabrics': '/Dashboard-Business-Manager',
  'viewer_fabrics': '/Dashboard-Business-Manager',
  
  'admin_mechanicsth': '/Dashboard-Business-Manager',
  'manager_mechanicsth': '/Dashboard-Business-Manager',
  'user_mechanicsth': '/Dashboard-Business-Manager',
  'viewer_mechanicsth': '/Dashboard-Business-Manager',
  
  'admin_mechanicsap': '/Dashboard-Business-Manager',
  'manager_mechanicsap': '/Dashboard-Business-Manager',
  'user_mechanicsap': '/Dashboard-Business-Manager',
  'viewer_mechanicsap': '/Dashboard-Business-Manager',
  
  'admin_hardwareth': '/Dashboard-Business-Manager',
  'manager_hardwareth': '/Dashboard-Business-Manager',
  'user_hardwareth': '/Dashboard-Business-Manager',
  'viewer_hardwareth': '/Dashboard-Business-Manager',
  
  'admin_hardwareap': '/Dashboard-Business-Manager',
  'manager_hardwareap': '/Dashboard-Business-Manager',
  'user_hardwareap': '/Dashboard-Business-Manager',
  'viewer_hardwareap': '/Dashboard-Business-Manager',
  
  'admin_autosth': '/Dashboard-Business-Manager',
  'manager_autosth': '/Dashboard-Business-Manager',
  'user_autosth': '/Dashboard-Business-Manager',
  'viewer_autosth': '/Dashboard-Business-Manager',
  
  'admin_autosap': '/Dashboard-Business-Manager',
  'manager_autosap': '/Dashboard-Business-Manager',
  'user_autosap': '/Dashboard-Business-Manager',
  'viewer_autosap': '/Dashboard-Business-Manager',
  
  'admin_pharmacy': '/Dashboard-Business-Manager',
  'manager_pharmacy': '/Dashboard-Business-Manager',
  'user_pharmacy': '/Dashboard-Business-Manager',
  'viewer_pharmacy': '/Dashboard-Business-Manager',
  
  'admin_pharmacytax': '/Dashboard-Business-Manager',
  'manager_pharmacytax': '/Dashboard-Business-Manager',
  'user_pharmacytax': '/Dashboard-Business-Manager',
  'viewer_pharmacytax': '/Dashboard-Business-Manager',
  
  'admin_cosmetics': '/Dashboard-Business-Manager',
  'manager_cosmetics': '/Dashboard-Business-Manager',
  'user_cosmetics': '/Dashboard-Business-Manager',
  'viewer_cosmetics': '/Dashboard-Business-Manager',
  
  'admin_solar': '/Dashboard-Business-Manager',
  'manager_solar': '/Dashboard-Business-Manager',
  'user_solar': '/Dashboard-Business-Manager',
  'viewer_solar': '/Dashboard-Business-Manager',
  
  'admin_electronics': '/Dashboard-Business-Manager',
  'manager_electronics': '/Dashboard-Business-Manager',
  'user_electronics': '/Dashboard-Business-Manager',
  'viewer_electronics': '/Dashboard-Business-Manager',
  
  'admin_appliances': '/Dashboard-Business-Manager',
  'manager_appliances': '/Dashboard-Business-Manager',
  'user_appliances': '/Dashboard-Business-Manager',
  'viewer_appliances': '/Dashboard-Business-Manager',
  
  'admin_pvc': '/Dashboard-Business-Manager',
  'manager_pvc': '/Dashboard-Business-Manager',
  'user_pvc': '/Dashboard-Business-Manager',
  'viewer_pvc': '/Dashboard-Business-Manager',
  
  'admin_salanty': '/Dashboard-Business-Manager',
  'manager_salanty': '/Dashboard-Business-Manager',
  'user_salanty': '/Dashboard-Business-Manager',
  'viewer_salanty': '/Dashboard-Business-Manager',
  
  'default': '/Dashboard-Business-Manager'
};

// ============================================
// TRANSLATIONS DICTIONARY
// ============================================
const TRANSLATIONS = {
  en: {
    welcome: "Welcome Back",
    slogan: "Unified Business Ecosystem",
    username: "Username",
    password: "Password",
    remember: "Remember me",
    forgot: "Forgot password?",
    signin: "Sign In",
    loading: "Authenticating...",
    contact: "Contact Administrator",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "Theme Color",
    access: "Access your unified workspace",
    clickHere: "Click here",
    user_inactive: "Account Inactive",
    user_inactive_message: "Your account is inactive. Please contact your administrator to reactivate your account.",
    user_suspended: "Account Suspended",
    user_suspended_message: "Your account has been suspended. Please contact your administrator.",
    subscription_expired: "⚠️ Subscription Expired",
    subscription_expired_message: "Your subscription has expired. Please renew to continue accessing the system.",
    subscription_cancelled: "Subscription Cancelled",
    subscription_cancelled_message: "Your subscription has been cancelled. Please contact your administrator to reactivate.",
    subscription_pending: "Subscription Pending",
    subscription_pending_message: "Your subscription is pending approval. Please wait for administrator approval.",
    no_subscription: "No Subscription Found",
    no_subscription_message: "No subscription found. Please purchase a subscription to access the system.",
    subscriptionStatus: "Subscription Status",
    totalSubscriptions: "Total",
    activeSubscriptions: "Active",
    expiredSubscriptions: "Expired",
    cancelledSubscriptions: "Cancelled",
    pendingSubscriptions: "Pending",
    daysRemaining: "Days Remaining",
    contactAdmin: "Please contact your administrator for assistance",
    close: "Close",
    renewNow: "Renew Now",
    userStatus: "Account Status",
    needHelp: "Need Help?",
    contactSupport: "Contact Support",
    selectLanguage: "Select Language",
    selectTheme: "Select Theme",
    darkMode: "Dark Mode",
    lightMode: "Light Mode"
  },
  ur: {
    welcome: "خوش آمدید",
    slogan: "متحد بزنس ایکو سسٹم",
    username: "صارف نام",
    password: "پاس ورڈ",
    remember: "مجھے یاد رکھیں",
    forgot: "پاس ورڈ بھول گئے؟",
    signin: "لاگ ان کریں",
    loading: "تصدیق ہو رہی ہے...",
    contact: "ایڈمنسٹریٹر سے رابطہ کریں",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "تھیم کا رنگ",
    access: "اپنے متحد کام کی جگہ تک رسائی حاصل کریں",
    clickHere: "یہاں کلک کریں",
    user_inactive: "اکاؤنٹ غیر فعال",
    user_inactive_message: "آپ کا اکاؤنٹ غیر فعال ہے۔ براہ کرم دوبارہ فعال کرنے کے لیے اپنے منتظم سے رابطہ کریں۔",
    user_suspended: "اکاؤنٹ معطل",
    user_suspended_message: "آپ کا اکاؤنٹ معطل کر دیا گیا ہے۔ براہ کرم اپنے منتظم سے رابطہ کریں۔",
    subscription_expired: "⚠️ سبسکرپشن میعاد ختم",
    subscription_expired_message: "آپ کی سبسکرپشن میعاد ختم ہو گئی ہے۔ براہ کرم سسٹم تک رسائی جاری رکھنے کے لیے تجدید کریں۔",
    subscription_cancelled: "سبسکرپشن منسوخ",
    subscription_cancelled_message: "آپ کی سبسکرپشن منسوخ کر دی گئی ہے۔ براہ کرم دوبارہ فعال کرنے کے لیے اپنے منتظم سے رابطہ کریں۔",
    subscription_pending: "سبسکرپشن زیر التواء",
    subscription_pending_message: "آپ کی سبسکرپشن منظوری کے زیر التواء ہے۔ براہ کرم منتظم کی منظوری کا انتظار کریں۔",
    no_subscription: "کوئی سبسکرپشن نہیں",
    no_subscription_message: "کوئی سبسکرپشن نہیں ملی۔ براہ کرم سسٹم تک رسائی کے لیے سبسکرپشن خریدیں۔",
    subscriptionStatus: "سبسکرپشن کی حالت",
    totalSubscriptions: "کل",
    activeSubscriptions: "فعال",
    expiredSubscriptions: "میعاد ختم",
    cancelledSubscriptions: "منسوخ",
    pendingSubscriptions: "زیر التواء",
    daysRemaining: "باقی دن",
    contactAdmin: "براہ کرم مدد کے لیے اپنے منتظم سے رابطہ کریں",
    close: "بند کریں",
    renewNow: "ابھی تجدید کریں",
    userStatus: "اکاؤنٹ کی حالت",
    needHelp: "مدد چاہیے؟",
    contactSupport: "سپورٹ سے رابطہ کریں",
    selectLanguage: "زبان منتخب کریں",
    selectTheme: "تھیم منتخب کریں",
    darkMode: "ڈارک موڈ",
    lightMode: "لائٹ موڈ"
  },
  ar: {
    welcome: "مرحبا بعودتك",
    slogan: "النظام الإيكولوجي للأعمال الموحد",
    username: "اسم المستخدم",
    password: "كلمة المرور",
    remember: "تذكرني",
    forgot: "نسيت كلمة المرور؟",
    signin: "تسجيل الدخول",
    loading: "جاري المصادقة...",
    contact: "الاتصال بالمسؤول",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "لون السمة",
    access: "الوصول إلى مساحة العمل الموحدة الخاصة بك",
    clickHere: "انقر هنا",
    user_inactive: "الحساب غير نشط",
    user_inactive_message: "حسابك غير نشط. يرجى الاتصال بالمسؤول لإعادة التنشيط.",
    user_suspended: "الحساب معلق",
    user_suspended_message: "تم تعليق حسابك. يرجى الاتصال بالمسؤول.",
    subscription_expired: "⚠️ انتهت صلاحية الاشتراك",
    subscription_expired_message: "انتهت صلاحية اشتراكك. يرجى التجديد لمواصلة الوصول إلى النظام.",
    subscription_cancelled: "تم إلغاء الاشتراك",
    subscription_cancelled_message: "تم إلغاء اشتراكك. يرجى الاتصال بالمسؤول لإعادة التنشيط.",
    subscription_pending: "الاشتراك معلق",
    subscription_pending_message: "اشتراكك في انتظار الموافقة. يرجى انتظار موافقة المسؤول.",
    no_subscription: "لا يوجد اشتراك",
    no_subscription_message: "لا يوجد اشتراك. يرجى شراء اشتراك للوصول إلى النظام.",
    subscriptionStatus: "حالة الاشتراك",
    totalSubscriptions: "الإجمالي",
    activeSubscriptions: "نشط",
    expiredSubscriptions: "منتهي الصلاحية",
    cancelledSubscriptions: "ملغى",
    pendingSubscriptions: "معلق",
    daysRemaining: "الأيام المتبقية",
    contactAdmin: "يرجى الاتصال بالمسؤول الخاص بك للحصول على المساعدة",
    close: "إغلاق",
    renewNow: "جدد الآن",
    userStatus: "حالة الحساب",
    needHelp: "بحاجة إلى مساعدة؟",
    contactSupport: "اتصل بالدعم",
    selectLanguage: "اختر اللغة",
    selectTheme: "اختر السمة",
    darkMode: "الوضع الداكن",
    lightMode: "الوضع الفاتح"
  },
  es: {
    welcome: "Bienvenido de nuevo",
    slogan: "Ecosistema Empresarial Unificado",
    username: "Nombre de usuario",
    password: "Contraseña",
    remember: "Recuérdame",
    forgot: "¿Olvidó su contraseña?",
    signin: "Iniciar sesión",
    loading: "Autenticando...",
    contact: "Contactar administrador",
    copyright: "© OurZest (Pvt LTD.) v3.0",
    theme: "Color del tema",
    access: "Accede a tu espacio de trabajo unificado",
    clickHere: "Haga clic aquí",
    user_inactive: "Cuenta Inactiva",
    user_inactive_message: "Su cuenta está inactiva. Contacte al administrador para reactivar.",
    user_suspended: "Cuenta Suspendida",
    user_suspended_message: "Su cuenta ha sido suspendida. Contacte al administrador.",
    subscription_expired: "⚠️ Suscripción Expirada",
    subscription_expired_message: "Su suscripción ha expirado. Renueve para continuar accediendo al sistema.",
    subscription_cancelled: "Suscripción Cancelada",
    subscription_cancelled_message: "Su suscripción ha sido cancelada. Contacte al administrador para reactivar.",
    subscription_pending: "Suscripción Pendiente",
    subscription_pending_message: "Su suscripción está pendiente de aprobación. Espere la aprobación del administrador.",
    no_subscription: "Sin Suscripción",
    no_subscription_message: "No se encontró una suscripción. Adquiera una suscripción para acceder al sistema.",
    subscriptionStatus: "Estado de Suscripción",
    totalSubscriptions: "Total",
    activeSubscriptions: "Activas",
    expiredSubscriptions: "Expiradas",
    cancelledSubscriptions: "Canceladas",
    pendingSubscriptions: "Pendientes",
    daysRemaining: "Días Restantes",
    contactAdmin: "Contacte a su administrador para asistencia",
    close: "Cerrar",
    renewNow: "Renovar Ahora",
    userStatus: "Estado de la Cuenta",
    needHelp: "¿Necesita ayuda?",
    contactSupport: "Contactar Soporte",
    selectLanguage: "Seleccionar idioma",
    selectTheme: "Seleccionar tema",
    darkMode: "Modo oscuro",
    lightMode: "Modo claro"
  }
};

const LANGUAGES = {
  en: { name: 'English', icon: '🇺🇸', dir: 'ltr' },
  ur: { name: 'اردو', icon: '🇵🇰', dir: 'rtl' },
  // ar: { name: 'عربي', icon: '🇸🇦', dir: 'rtl' },
  // es: { name: 'Español', icon: '🇪🇸', dir: 'ltr' }
};

// ============================================
// THEME COLORS (Enhanced)
// ============================================
const THEME_COLORS = [
  { id: 'amber-glow', color: '#f59e0b', name: 'Amber Glow' },
  { id: 'coral-blaze', color: '#ff6b6b', name: 'Coral Blaze' },
  { id: 'lime-burst', color: '#84cc16', name: 'Lime Burst' },
  { id: 'orange-zest', color: '#ea580c', name: 'Orange Zest' },
  { id: 'ruby-red', color: '#e11d48', name: 'Ruby Red' },
  { id: 'rose-gold', color: '#f43f5e', name: 'Rose Gold' },
  { id: 'tangerine', color: '#f97316', name: 'Tangerine' },
  { id: 'sunset-orange', color: '#fb923c', name: 'Sunset Orange' },
  { id: 'cinnamon', color: '#d97706', name: 'Cinnamon' },
  { id: 'marigold', color: '#eab308', name: 'Marigold' },
  { id: 'ocean-cyan', color: '#06b6d4', name: 'Ocean Cyan' },
  { id: 'deep-teal', color: '#14b8a6', name: 'Deep Teal' },
  { id: 'emerald-green', color: '#10b981', name: 'Emerald Green' },
  { id: 'forest-green', color: '#059669', name: 'Forest Green' },
  { id: 'sapphire-blue', color: '#2563eb', name: 'Sapphire Blue' },
  { id: 'royal-blue', color: '#3b82f6', name: 'Royal Blue' },
  { id: 'sky-blue', color: '#0ea5e9', name: 'Sky Blue' },
  { id: 'arctic-blue', color: '#38bdf8', name: 'Arctic Blue' },
  { id: 'mint-green', color: '#34d399', name: 'Mint Green' },
  { id: 'aqua-marine', color: '#2dd4bf', name: 'Aqua Marine' },
  { id: 'platinum', color: '#9ca3af', name: 'Platinum' },
  { id: 'gold-trim', color: '#d4a574', name: 'Gold Trim' },
  { id: 'silver-shadow', color: '#94a3b8', name: 'Silver Shadow' },
  { id: 'indigo-night', color: '#4f46e5', name: 'Indigo Night' },
  { id: 'cyber-yellow', color: '#facc15', name: 'Cyber Yellow' },
  { id: 'electric-lime', color: '#a3e635', name: 'Electric Lime' },
  { id: 'neon-cyan', color: '#22d3ee', name: 'Neon Cyan' },
  { id: 'laser-red', color: '#ef4444', name: 'Laser Red' },
  { id: 'matrix-green', color: '#4ade80', name: 'Matrix Green' },
  { id: 'digital-blue', color: '#60a5fa', name: 'Digital Blue' },
  { id: 'terracotta', color: '#c2410c', name: 'Terracotta' },
  { id: 'olive-green', color: '#65a30d', name: 'Olive Green' },
  { id: 'sage', color: '#84cc16', name: 'Sage' },
  { id: 'sandstone', color: '#d97706', name: 'Sandstone' },
  { id: 'clay', color: '#b45309', name: 'Clay' },
  { id: 'moss-green', color: '#4d7c0f', name: 'Moss Green' },
  { id: 'brick-red', color: '#dc2626', name: 'Brick Red' },
  { id: 'chestnut', color: '#92400e', name: 'Chestnut' },
  { id: 'hazel', color: '#a16207', name: 'Hazel' },
  { id: 'mahogany', color: '#991b1b', name: 'Mahogany' }
];

// ============================================
// FEATURES
// ============================================
const FEATURES = [
  { icon: Pill, text: 'Pharmacy & Medical Store' },
  { icon: Sparkles, text: 'Cosmetics & Beauty Shop' },
  { icon: ShoppingCart, text: 'General Store (Kirana)' },
  { icon: ScissorsIcon, text: 'Tailor & Fabric Shop' },
  { icon: Shirt, text: 'Clothing (Readymade)' },
  { icon: Droplet, text: 'Paint & Hardware Store' },
  { icon: Wrench, text: 'Auto Workshop' },
  { icon: Car, text: 'Spare Parts Shop' },
  { icon: Bike, text: 'Motorcycle Workshop' },
  { icon: Home, text: 'Home Decor & Furniture' },
  { icon: Package, text: 'Electronics Shop' },
  { icon: Coffee, text: 'Restaurant & Cafe' }
];

// ============================================
// 3D SUBSCRIPTION POPUP
// ============================================
const SubscriptionPopup3D = ({ 
  isOpen, 
  onClose, 
  blockReason, 
  message, 
  subscriptionSummary, 
  subscriptions,
  userStatus,
  subscriptionStatus,
  translations,
  onRenew
}) => {
  const cardRef = useRef(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });

  if (!isOpen) return null;

  const getBlockConfig = () => {
    const configs = {
      'subscription_expired': {
        icon: 'fa-exclamation-triangle',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: translations.subscription_expired || '⚠️ Subscription Expired',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      },
      'subscription_cancelled': {
        icon: 'fa-times-circle',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: '#f59e0b',
        title: translations.subscription_cancelled || 'Subscription Cancelled',
        glowColor: 'rgba(245, 158, 11, 0.2)'
      },
      'subscription_pending': {
        icon: 'fa-hourglass-half',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: '#f59e0b',
        title: translations.subscription_pending || 'Subscription Pending',
        glowColor: 'rgba(245, 158, 11, 0.2)'
      },
      'no_subscription': {
        icon: 'fa-file-invoice',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.12)',
        border: '#f59e0b',
        title: translations.no_subscription || 'No Subscription Found',
        glowColor: 'rgba(245, 158, 11, 0.2)'
      },
      'user_inactive': {
        icon: 'fa-user-slash',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: translations.user_inactive || 'Account Inactive',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      },
      'user_suspended': {
        icon: 'fa-ban',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: translations.user_suspended || 'Account Suspended',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      },
      'default': {
        icon: 'fa-exclamation-triangle',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: '#ef4444',
        title: 'Access Denied',
        glowColor: 'rgba(239, 68, 68, 0.2)'
      }
    };
    return configs[blockReason] || configs['default'];
  };

  const config = getBlockConfig();

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
  };

  return createPortal(
    <div className="oz-popup-3d-overlay" onClick={onClose}>
      <div className="oz-popup-3d-container" onClick={(e) => e.stopPropagation()}>
        <div 
          ref={cardRef}
          className="oz-popup-3d-card"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            transform: `perspective(800px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
          }}
        >
          <div className="oz-popup-3d-glow" style={{ background: config.glowColor }}></div>
          
          <div className="oz-popup-3d-icon-wrapper">
            <div className="oz-popup-3d-icon" style={{ background: config.bg, borderColor: config.border }}>
              <i className={`fas ${config.icon}`} style={{ color: config.color }}></i>
            </div>
          </div>

          <div className="oz-popup-3d-content">
            <h2 className="oz-popup-3d-title" style={{ color: config.color }}>
              {config.title}
            </h2>
            
            <p className="oz-popup-3d-message">{message}</p>

            {subscriptionSummary && (
              <div className="oz-popup-3d-summary">
                <div className="oz-popup-3d-summary-grid">
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-list"></i>
                      {translations.totalSubscriptions || 'Total'}
                    </span>
                    <span className="oz-popup-3d-summary-value">
                      {subscriptionSummary.total || 0}
                    </span>
                  </div>
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                      {translations.activeSubscriptions || 'Active'}
                    </span>
                    <span className="oz-popup-3d-summary-value oz-popup-3d-active">
                      {subscriptionSummary.active || 0}
                    </span>
                  </div>
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i>
                      {translations.expiredSubscriptions || 'Expired'}
                    </span>
                    <span className="oz-popup-3d-summary-value oz-popup-3d-expired">
                      {subscriptionSummary.expired || 0}
                    </span>
                  </div>
                  <div className="oz-popup-3d-summary-item">
                    <span className="oz-popup-3d-summary-label">
                      <i className="fas fa-ban" style={{ color: '#f59e0b' }}></i>
                      {translations.cancelledSubscriptions || 'Cancelled'}
                    </span>
                    <span className="oz-popup-3d-summary-value oz-popup-3d-cancelled">
                      {subscriptionSummary.cancelled || 0}
                    </span>
                  </div>
                </div>
                
                {subscriptionSummary.days_until_expiry !== null && subscriptionSummary.days_until_expiry >= 0 && (
                  <div className="oz-popup-3d-days">
                    <i className="fas fa-clock"></i>
                    <span>
                      {translations.daysRemaining || 'Days Remaining'}: 
                      <strong>{subscriptionSummary.days_until_expiry}</strong> days
                    </span>
                  </div>
                )}
              </div>
            )}

            {(blockReason === 'subscription_expired' || blockReason === 'no_subscription') && (
              <button className="oz-popup-3d-renew-btn" onClick={onRenew}>
                <i className="fas fa-sync-alt"></i>
                <span>{blockReason === 'no_subscription' ? 'Get Subscription' : (translations.renewNow || 'Renew Now')}</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            )}

            <button className="oz-popup-3d-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
              <span>{translations.close || 'Close'}</span>
            </button>

            <div className="oz-popup-3d-footer">
              <p>
                <i className="fas fa-headset"></i>
                {translations.needHelp || 'Need Help?'} 
                <a href="https://wa.me/message/6D64QLRVE5YGG1" target="_blank" rel="noopener noreferrer">
                  {translations.contactSupport || 'Contact Support'}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ============================================
// LANGUAGE & THEME SELECTOR COMPONENT
// ============================================
const LanguageThemeSelector = ({ 
  language, 
  setLanguage, 
  selectedTheme, 
  setSelectedTheme, 
  isDark, 
  setIsDark, 
  texts,
  currentColor 
}) => {
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const languageRef = useRef(null);
  const themeRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (languageRef.current && !languageRef.current.contains(e.target)) {
        setIsLanguageOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target)) {
        setIsThemeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (langCode) => {
    setLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const handleThemeSelect = (themeId) => {
    setSelectedTheme(themeId);
    setIsThemeOpen(false);
  };

  const handleModeToggle = (mode) => {
    setIsDark(mode === 'dark');
  };

  const currentLanguage = LANGUAGES[language] || LANGUAGES.en;
  const currentTheme = THEME_COLORS.find(t => t.id === selectedTheme) || THEME_COLORS[0];

  return (
    <div className="auth-language-theme-selector">
      {/* Language Selector */}
      <div className="auth-selector-group" ref={languageRef}>
        <button 
          className="auth-selector-btn"
          onClick={() => setIsLanguageOpen(!isLanguageOpen)}
          style={{ borderColor: isLanguageOpen ? currentColor : 'var(--auth-border)' }}
        >
          <span className="auth-selector-flag">{currentLanguage.icon}</span>
          <span className="auth-selector-label">{currentLanguage.name}</span>
          <ChevronDown size={14} className={`auth-selector-chevron ${isLanguageOpen ? 'open' : ''}`} />
        </button>
        
        {isLanguageOpen && (
          <div className="auth-selector-dropdown">
            {Object.entries(LANGUAGES).map(([code, lang]) => (
              <button
                key={code}
                className={`auth-selector-option ${language === code ? 'active' : ''}`}
                onClick={() => handleLanguageSelect(code)}
                style={{ 
                  background: language === code ? `${currentColor}15` : 'transparent',
                  borderColor: language === code ? currentColor : 'transparent'
                }}
              >
                <span className="auth-selector-option-flag">{lang.icon}</span>
                <span className="auth-selector-option-name">{lang.name}</span>
                {language === code && <CheckCircle size={14} style={{ color: currentColor, marginLeft: 'auto' }} />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Theme & Mode Selector */}
      <div className="auth-selector-group" ref={themeRef}>
        <button 
          className="auth-selector-btn auth-selector-theme-btn"
          onClick={() => setIsThemeOpen(!isThemeOpen)}
          style={{ borderColor: isThemeOpen ? currentColor : 'var(--auth-border)' }}
        >
          <Palette size={16} style={{ color: currentColor }} />
          <span className="auth-selector-label">{texts.theme || 'Theme'}</span>
          <ChevronDown size={14} className={`auth-selector-chevron ${isThemeOpen ? 'open' : ''}`} />
        </button>
        
        {isThemeOpen && (
          <div className="auth-selector-dropdown auth-selector-theme-dropdown">
            {/* Mode Toggle */}
            <div className="auth-selector-mode-group">
              <button
                className={`auth-selector-mode-btn ${!isDark ? 'active' : ''}`}
                onClick={() => handleModeToggle('light')}
                style={{ 
                  borderColor: !isDark ? currentColor : 'transparent',
                  background: !isDark ? `${currentColor}10` : 'transparent'
                }}
              >
                <Sun size={14} />
                <span>{texts.lightMode || 'Light'}</span>
              </button>
              <button
                className={`auth-selector-mode-btn ${isDark ? 'active' : ''}`}
                onClick={() => handleModeToggle('dark')}
                style={{ 
                  borderColor: isDark ? currentColor : 'transparent',
                  background: isDark ? `${currentColor}10` : 'transparent'
                }}
              >
                <Moon size={14} />
                <span>{texts.darkMode || 'Dark'}</span>
              </button>
            </div>

            {/* Color Grid */}
            <div className="auth-selector-color-grid">
              {THEME_COLORS.map((theme) => (
                <button
                  key={theme.id}
                  className={`auth-selector-color-btn ${selectedTheme === theme.id ? 'active' : ''}`}
                  style={{
                    backgroundColor: theme.color,
                    borderColor: selectedTheme === theme.id ? '#ffffff' : 'transparent',
                    boxShadow: selectedTheme === theme.id ? `0 0 12px ${theme.color}60` : 'none'
                  }}
                  onClick={() => handleThemeSelect(theme.id)}
                  title={theme.name}
                >
                  {selectedTheme === theme.id && <span className="auth-selector-color-check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN LOGIN COMPONENT
// ============================================
function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [toast, setToast] = useState(null);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState('amber-glow');
  const [isDark, setIsDark] = useState(false);
  const [logoError, setLogoError] = useState(false);
  
  // Language state
  const [language, setLanguage] = useState('en');
  const [texts, setTexts] = useState(TRANSLATIONS.en);
  const [preferencesReady, setPreferencesReady] = useState(false);
  
  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    blockReason: '',
    message: '',
    summary: null,
    subscriptions: null,
    userStatus: '',
    subscriptionStatus: ''
  });

  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const currentColor = THEME_COLORS.find(t => t.id === selectedTheme)?.color || '#f59e0b';
  // Inline custom properties make the animated background follow the selected
  // theme even if an old browser cache still contains previous CSS values.
  const loginThemeStyle = {
    '--auth-theme-color': currentColor,
    '--auth-theme-dark': `${currentColor}cc`,
    '--auth-theme-glow': `${currentColor}38`,
    '--auth-theme-soft': `${currentColor}18`,
  };

  // ============================================
  // STORAGE KEYS - PERSISTENT PREFERENCES
  // ============================================
  const STORAGE_KEYS = {
    THEME: 'auth_theme_id',
    DARK_MODE: 'auth_dark_mode',
    LANGUAGE: 'auth_language',
    REMEMBER_ME: 'auth_remember_me',
    USERNAME: 'auth_saved_username'
  };

  // ============================================
  // INITIALIZE FROM LOCAL STORAGE (PERSISTENT)
  // ============================================
  useEffect(() => {
    // Load theme preference
    const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME) || localStorage.getItem('ourzest_theme_id');
    if (savedTheme && THEME_COLORS.find(t => t.id === savedTheme)) {
      setSelectedTheme(savedTheme);
    }

    // Load dark mode preference
    const savedDarkMode = localStorage.getItem(STORAGE_KEYS.DARK_MODE) ?? localStorage.getItem('ourzest_darkMode');
    if (savedDarkMode !== null) {
      setIsDark(savedDarkMode === 'true');
    }

    // Load language preference
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE) || localStorage.getItem('ourzest_language');
    if (savedLanguage) {
      const safeLanguage = savedLanguage === 'ur' ? 'ur' : 'en';
      setLanguage(safeLanguage);
      setTexts(TRANSLATIONS[safeLanguage]);
      applyLanguageDirection(safeLanguage);
    }

    // Load remember me and username
    const savedRemember = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) || localStorage.getItem('ourzest_remember') || localStorage.getItem('rememberMe');
    const savedUsername = localStorage.getItem(STORAGE_KEYS.USERNAME) || localStorage.getItem('ourzest_username') || localStorage.getItem('savedUsername');
    if (savedRemember === 'true' && savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    // Apply theme colors
    applyThemeColors(currentColor);
    setPreferencesReady(true);
  }, []);

  // ============================================
  // UPDATE CSS VARIABLES WHEN THEME CHANGES
  // ============================================
  useEffect(() => {
    applyThemeColors(currentColor);
  }, [currentColor]);

  // ============================================
  // APPLY THEME COLORS
  // ============================================
  const applyThemeColors = (color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = '#f59e0b';
    }
    
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    const lighten = (amount) => {
      const newR = Math.min(255, r + amount);
      const newG = Math.min(255, g + amount);
      const newB = Math.min(255, b + amount);
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    };
    
    const darken = (amount) => {
      const newR = Math.max(0, r - amount);
      const newG = Math.max(0, g - amount);
      const newB = Math.max(0, b - amount);
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    };
    
    document.documentElement.style.setProperty('--oz-primary-color', color);
    document.documentElement.style.setProperty('--oz-primary-dark', darken(40));
    document.documentElement.style.setProperty('--oz-primary-light', lighten(40));
    document.documentElement.style.setProperty('--oz-primary-ultralight', lighten(80));
    document.documentElement.style.setProperty('--oz-primary-gradient', `linear-gradient(135deg, ${color}, ${darken(30)})`);
    document.documentElement.style.setProperty('--oz-primary-shadow', `${color}20`);
  };

  // ============================================
  // APPLY LANGUAGE DIRECTION
  // ============================================
  const applyLanguageDirection = (langCode) => {
    const safeLanguage = langCode === 'ur' ? 'ur' : 'en';
    const langData = LANGUAGES[safeLanguage];
    if (langData) {
      document.documentElement.dir = langData.dir;
      document.documentElement.lang = safeLanguage;
      document.body.dir = langData.dir;
      document.documentElement.style.direction = langData.dir;
      document.documentElement.classList.toggle('ourzest-rtl', safeLanguage === 'ur');
      document.body.classList.toggle('ourzest-rtl', safeLanguage === 'ur');
    }
  };

  // ============================================
  // SAVE PREFERENCES TO LOCAL STORAGE (PERSISTENT)
  // ============================================
  const savePreferences = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.THEME, selectedTheme);
    localStorage.setItem(STORAGE_KEYS.DARK_MODE, String(isDark));
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
    // Keep the dashboard and AuthContext keys in sync. These are preferences,
    // not session credentials, and must survive logout.
    const theme = THEME_COLORS.find((item) => item.id === selectedTheme)?.color || '#f59e0b';
    localStorage.setItem('ourzest_theme', theme);
    localStorage.setItem('ourzest_theme_id', selectedTheme);
    localStorage.setItem('ourzest_darkMode', String(isDark));
    localStorage.setItem('ourzest_language', language === 'ur' ? 'ur' : 'en');
    
    if (rememberMe && username.trim()) {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
      localStorage.setItem(STORAGE_KEYS.USERNAME, username.trim());
      localStorage.setItem('ourzest_remember', 'true');
      localStorage.setItem('ourzest_username', username.trim());
      localStorage.setItem('rememberMe', 'true');
      localStorage.setItem('savedUsername', username.trim());
    } else {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'false');
      localStorage.setItem('ourzest_remember', 'false');
      localStorage.setItem('rememberMe', 'false');
      if (!rememberMe) {
        localStorage.removeItem(STORAGE_KEYS.USERNAME);
        localStorage.removeItem('ourzest_username');
        localStorage.removeItem('savedUsername');
      }
    }
  }, [selectedTheme, isDark, language, rememberMe, username]);

  // ============================================
  // SAVE PREFERENCES WHEN THEY CHANGE
  // ============================================
  useEffect(() => {
    // Don't save on initial load, only on user changes
    if (preferencesReady && selectedTheme && language) {
      savePreferences();
    }
  }, [preferencesReady, selectedTheme, isDark, language, savePreferences]);

  // ============================================
  // HANDLE LANGUAGE CHANGE
  // ============================================
  const handleLanguageChange = (langCode) => {
    const safeLanguage = langCode === 'ur' ? 'ur' : 'en';
    setLanguage(safeLanguage);
    setTexts(TRANSLATIONS[safeLanguage]);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, safeLanguage);
    localStorage.setItem('ourzest_language', safeLanguage);
    applyLanguageDirection(safeLanguage);
  };

  // ============================================
  // HANDLE THEME CHANGE
  // ============================================
  const handleThemeChange = (themeId) => {
    setSelectedTheme(themeId);
    const theme = THEME_COLORS.find((item) => item.id === themeId);
    if (theme) localStorage.setItem('ourzest_theme', theme.color);
  };

  // ============================================
  // HANDLE DARK MODE TOGGLE
  // ============================================
  const handleDarkModeToggle = (dark) => {
    setIsDark(dark);
    localStorage.setItem('ourzest_darkMode', String(Boolean(dark)));
  };

  // ============================================
  // ROTATE FEATURES
  // ============================================
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % FEATURES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // ============================================
  // LOCK ZOOM
  // ============================================
  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousContent = viewportMeta ? viewportMeta.getAttribute('content') : null;
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.setAttribute('name', 'viewport');
      document.head.appendChild(viewportMeta);
    }
    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover'
    );

    const blockMultiTouch = (e) => {
      if (e.touches && e.touches.length > 1) e.preventDefault();
    };

    let lastTouchEnd = 0;
    const blockDoubleTapZoom = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 350) e.preventDefault();
      lastTouchEnd = now;
    };

    const blockGestureZoom = (e) => e.preventDefault();
    const blockCtrlWheelZoom = (e) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    };

    document.addEventListener('touchmove', blockMultiTouch, { passive: false });
    document.addEventListener('touchend', blockDoubleTapZoom, false);
    document.addEventListener('gesturestart', blockGestureZoom, { passive: false });
    document.addEventListener('wheel', blockCtrlWheelZoom, { passive: false });

    return () => {
      document.removeEventListener('touchmove', blockMultiTouch);
      document.removeEventListener('touchend', blockDoubleTapZoom);
      document.removeEventListener('gesturestart', blockGestureZoom);
      document.removeEventListener('wheel', blockCtrlWheelZoom);
      if (previousContent !== null) {
        viewportMeta.setAttribute('content', previousContent);
      }
    };
  }, []);

  const showToast = (type, title, message) => {
    setToast({ type, title, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ============================================
  // BUTTON HOVER EFFECT
  // ============================================
  useEffect(() => {
    const button = document.querySelector('.auth-login-button');
    if (!button) return;

    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      button.style.setProperty('--mouse-x', `${x}%`);
      button.style.setProperty('--mouse-y', `${y}%`);
    };

    const handleClick = (e) => {
      const rect = button.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      button.style.setProperty('--click-x', `${x}%`);
      button.style.setProperty('--click-y', `${y}%`);
    };

    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('click', handleClick);

    return () => {
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('click', handleClick);
    };
  }, []);

  // ============================================
  // GET DASHBOARD ROUTE
  // ============================================
  const getDashboardRoute = (role) => {
    if (role && ROUTES[role]) return ROUTES[role];
    return '/dashboard';
  };

  const handleRenew = () => {
    setShowPopup(false);
    window.open('https://wa.me/message/6D64QLRVE5YGG1', '_blank');
  };

  // ============================================
  // HANDLE LOGIN
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!username.trim()) {
      showToast('warning', 'Username Required', texts.username + ' is required');
      setIsLoading(false);
      return;
    }

    if (!password) {
      showToast('warning', 'Password Required', texts.password + ' is required');
      setIsLoading(false);
      return;
    }

    try {
      const loginUrl = `https://ourzestapp.online/oz_backend/login.php?_=${Date.now()}`;
      const response = await fetch(loginUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(secureLoginPayload({
          username: username.trim(), 
          password: password,
          remember: rememberMe,
          theme_color: currentColor,
          language: language
        }, 'root-portal')),
      });
      
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(
          `Login API returned HTTP ${response.status} with an invalid JSON response.`
        );
      }
      
      if (!data.success && data.login_status) {
        setPopupData({
          blockReason: data.login_status,
          message: data.message,
          summary: data.subscription_summary || null,
          subscriptions: data.subscriptions || null,
          userStatus: data.user_status || '',
          subscriptionStatus: data.subscription_status || ''
        });
        setShowPopup(true);
        setIsLoading(false);
        return;
      }
      
      if (data.success) {
        // Save preferences before login
        savePreferences();

        const legacySessionId = data.session_id || data.user?.session_id || null;
        if (legacySessionId) {
          localStorage.setItem('session_id', legacySessionId);
        }

        if (data.secure_session?.session_token) {
          acceptSecureLogin(data);
        } else {
          await upgradeLegacySession(legacySessionId, 'root-portal');
        }

        const userRole = data.role;
        const isSuperAdmin = data.is_super_admin || userRole === 'super_admin' || userRole === 'superadmin';
        
        const route = getDashboardRoute(userRole);
        
        if (!route) {
          showToast('error', 'Access Denied', `No dashboard available for role: ${userRole}`);
          setIsLoading(false);
          return;
        }
        
        const userData = {
          id: data.user?.id,
          session_id: legacySessionId,
          application_id: data.secure_session?.application_id || null,
          username: data.user?.username || username.trim(),
          fullname: data.user?.fullname || username.trim(),
          role: userRole,
          themeColor: currentColor,
          language: language,
          businessName: data.user?.business_name || "OurZest Corp",
          email: data.user?.email || `${username.trim()}@oz.com`,
          shop_id: data.user?.shop_id,
          shop_name: data.user?.shop_name,
          shop_code: data.user?.shop_code,
          status: data.user?.status || 'active',
          is_super_admin: isSuperAdmin || false,
          subscription: data.subscription || null,
          login_status: data.login_status || 'active'
        };
        
        localStorage.setItem('user_role', userRole);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem("auth_timestamp", Date.now().toString());
        
        if (isSuperAdmin) {
          localStorage.setItem('is_super_admin', 'true');
        }
        
        if (data.subscription) {
          localStorage.setItem('user_subscription', JSON.stringify(data.subscription));
        }
        
        setUser(userData);
        
        const welcomeName = userData.fullname || userData.username;
        const welcomeMsg = isSuperAdmin ? 'Super Admin login successful!' : `Hello ${welcomeName}!`;
        showToast('success', isSuperAdmin ? 'Welcome Super Admin' : 'Welcome Back', welcomeMsg);
        
        setTimeout(() => {
          navigate(route, { replace: true });
        }, 1500);
        
      } else {
        showToast('error', 'Access Denied', data.message || 'Invalid credentials');
        setPassword('');
        setIsLoading(false);
      }
      
    } catch (err) {
      console.error('Login error:', err);
      showToast('error', 'System Error', 'Cannot connect to server. Please check your connection.');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) handleSubmit(e);
  };

  const CurrentFeatureIcon = FEATURES[currentFeature]?.icon || Activity;

  const getToastIcon = (type) => {
    if (type === 'success') return <CheckCircle size={16} />;
    return <AlertCircle size={16} />;
  };

  const getToastStyle = (type) => {
    if (type === 'success') {
      return {
        iconBg: 'rgba(16, 185, 129, 0.15)',
        iconColor: '#10b981',
        borderColor: 'rgba(16, 185, 129, 0.2)'
      };
    }
    if (type === 'warning') {
      return {
        iconBg: 'rgba(245, 158, 11, 0.15)',
        iconColor: '#f59e0b',
        borderColor: 'rgba(245, 158, 11, 0.2)'
      };
    }
    return {
      iconBg: 'rgba(239, 68, 68, 0.15)',
      iconColor: '#ef4444',
      borderColor: 'rgba(239, 68, 68, 0.2)'
    };
  };

  return (
    <div
      className={`auth-login-container ${isDark ? 'dark' : 'light'} ${language === 'ur' ? 'auth-urdu' : ''}`}
      ref={containerRef}
      style={loginThemeStyle}
    >
      {/* 3D Subscription Popup */}
      <SubscriptionPopup3D 
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        blockReason={popupData.blockReason}
        message={popupData.message}
        subscriptionSummary={popupData.summary}
        subscriptions={popupData.subscriptions}
        userStatus={popupData.userStatus}
        subscriptionStatus={popupData.subscriptionStatus}
        translations={texts}
        onRenew={handleRenew}
      />
      
      {/* Gradient Background Overlay */}
      <div className="auth-gradient-background">
        <div className="auth-gradient-layer auth-gradient-layer-1" />
        <div className="auth-gradient-layer auth-gradient-layer-2" />
        <div className="auth-gradient-layer auth-gradient-layer-3" />
        <div className="auth-gradient-layer auth-gradient-layer-4" />
      </div>

      {/* Toast */}
      {toast && (
        <div className={`auth-login-toast ${toast.type}`} style={{ borderColor: getToastStyle(toast.type).borderColor }}>
          <div className="auth-login-toast-content">
            <div className="auth-login-toast-icon" style={{
              background: getToastStyle(toast.type).iconBg,
              color: getToastStyle(toast.type).iconColor
            }}>
              {getToastIcon(toast.type)}
            </div>
            <div className="auth-login-toast-text">
              <div className="auth-login-toast-title">{toast.title}</div>
              <div className="auth-login-toast-message">{toast.message}</div>
            </div>
            <button className="auth-login-toast-close" onClick={() => setToast(null)}>
              <X size={16} />
            </button>
          </div>
          <div className="auth-login-toast-timer">
            <div className="auth-login-toast-progress-bar" style={{
              background: toast.type === 'success' ? 'linear-gradient(90deg, #10b981, #059669)'
                : toast.type === 'warning' ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                : 'linear-gradient(90deg, #ef4444, #dc2626)'
            }} />
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="auth-login-card" ref={cardRef}>
        <div className="auth-login-brand">
          <div className="auth-brand-icon-wrapper">
            <div className="auth-brand-icon-ring">
              {logoError ? (
                <div className="auth-brand-icon" style={{ color: currentColor }}>
                  <Activity size={32} />
                </div>
              ) : (
                <img
                  src={logo}
                  alt="OURZEST"
                  className="auth-brand-logo"
                  onError={() => setLogoError(true)}
                />
              )}
            </div>
          </div>
          <h1 className="auth-brand-title">
            OURZEST-APP
          </h1>
          <p className="auth-brand-subtitle">{texts.access}</p>
        </div>

        {/* Language & Theme Selector - Moved prominently above the form */}
        <LanguageThemeSelector
          language={language}
          setLanguage={handleLanguageChange}
          selectedTheme={selectedTheme}
          setSelectedTheme={handleThemeChange}
          isDark={isDark}
          setIsDark={handleDarkModeToggle}
          texts={texts}
          currentColor={currentColor}
        />

      

        <form onSubmit={handleSubmit} className="auth-login-form" autoComplete="on">
          <div className="auth-login-form-group">
            <div className="auth-login-input-wrapper">
              <User className="auth-login-input-icon" size={18} />
              <input
                type="text"
                id="username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Username"
                autoComplete="username"
                autoFocus
                disabled={isLoading}
                className="auth-login-input"
                style={{ borderColor: `${currentColor}20` }}
              />
              <label htmlFor="username" style={{ color: 'var(--auth-label-color)' }}>{texts.username}</label>
            </div>
          </div>

          <div className="auth-login-form-group">
            <div className="auth-login-input-wrapper">
              <Lock className="auth-login-input-icon" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Password"
                autoComplete="current-password"
                disabled={isLoading}
                className="auth-login-input"
                style={{ borderColor: `${currentColor}20` }}
              />
              <label htmlFor="password" style={{ color: 'var(--auth-label-color)' }}>{texts.password}</label>
              <button
                type="button"
                className="auth-login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="auth-login-options">
            <label className="auth-login-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
                style={{ accentColor: currentColor }}
              />
              <span>{texts.remember}</span>
            </label>
            <button
              type="button"
              className="auth-login-forgot-password"
              style={{ color: `${currentColor}80` }}
              onClick={() => showToast('warning', 'Reset Password', texts.forgot + '? Please contact your administrator')}
            >
              {texts.forgot}
            </button>
          </div>

          <button
            type="submit"
            className={`auth-login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
            style={{
              background: `linear-gradient(135deg, ${currentColor}, ${currentColor}dd)`,
              boxShadow: `0 10px 40px ${currentColor}30`
            }}
          >
            <span className="auth-login-button-text">
              {isLoading ? texts.loading : texts.signin}
            </span>
            {isLoading ? (
              <span className="auth-login-spinner" />
            ) : (
              <ArrowRight size={18} className="auth-login-button-icon" />
            )}
          </button>
        </form>

        <div className="auth-login-footer">
          <div className="auth-login-footer-links">
            <span>{texts.copyright}</span>
            <span className="auth-footer-divider">•</span>
            <button>Privacy</button>
            <span className="auth-footer-divider">•</span>
            <button>Terms</button>
            <span className="auth-footer-divider">•</span>
            <button>Support</button>
          </div>
          <div className="auth-login-version">
            <span>v3.0 Enterprise</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
